import type { Task } from "../tasks/types.js";
import type { RecurrenceRule } from "../recurrence/types.js";
import type { RecurrenceOverride } from "../recurrence/overrides/types.js";
import type { Label } from "../labels/types.js";
import type { Topic } from "../topics/types.js";
import type { Calendar } from "../calendars/types.js";
import type { ThemeConfig } from "../theme/types.js";
import { BACKUP_VERSION, type BackupEnvelope, type BackupPreferences } from "./types.js";

export type ValidationResult =
  | { ok: true; envelope: BackupEnvelope }
  | { ok: false; error: string };

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === "string";
const isStrOrNull = (v: unknown): v is string | null => v === null || typeof v === "string";

/** Every persisted row carries the sync record shape. */
function hasRecordShape(r: Obj): boolean {
  return isStr(r.id) && isStr(r.created_at) && isStr(r.updated_at) && isStrOrNull(r.deleted_at);
}

/**
 * Hand-written guard (no zod — core stays dependency-free). Validates the
 * envelope shape, version, and the required fields on every row. On success
 * returns a typed envelope with any `virtual` flag stripped off tasks.
 */
export function parseBackup(raw: unknown): ValidationResult {
  if (!isObj(raw)) return fail("file is not a JSON object");
  if (raw.version !== BACKUP_VERSION) return fail(`unsupported backup version: ${String(raw.version)}`);

  const tasks = checkArray(raw.tasks, "tasks", isTask);
  if (tasks.error) return fail(tasks.error);
  const rules = checkArray(raw.recurrenceRules, "recurrenceRules", isRule);
  if (rules.error) return fail(rules.error);
  const overrides = checkArray(raw.recurrenceOverrides, "recurrenceOverrides", isOverride);
  if (overrides.error) return fail(overrides.error);
  const labels = checkArray(raw.labels, "labels", isLabel);
  if (labels.error) return fail(labels.error);
  // Topics + calendars are optional for backward-compat: older backups predate
  // them, so an absent key is treated as an empty list (not an error).
  const topics = checkOptionalArray(raw.topics, "topics", isTopic);
  if (topics.error) return fail(topics.error);
  const calendars = checkOptionalArray(raw.calendars, "calendars", isCalendar);
  if (calendars.error) return fail(calendars.error);
  const themes = checkOptionalArray(raw.themes, "themes", isTheme);
  if (themes.error) return fail(themes.error);

  const envelope: BackupEnvelope = {
    version: BACKUP_VERSION,
    exportedAt: isStr(raw.exportedAt) ? raw.exportedAt : "",
    tasks: (tasks.rows as unknown as Task[]).map(stripVirtual),
    recurrenceRules: rules.rows as unknown as RecurrenceRule[],
    recurrenceOverrides: overrides.rows as unknown as RecurrenceOverride[],
    labels: labels.rows as unknown as Label[],
    topics: topics.rows as unknown as Topic[],
    calendars: calendars.rows as unknown as Calendar[],
    themes: themes.rows as unknown as ThemeConfig[],
  };
  // Preferences are optional and loosely shaped — pass the object through; the
  // app validates each field per-key on apply (and ignores anything unknown).
  if (isObj(raw.preferences)) envelope.preferences = raw.preferences as BackupPreferences;

  return { ok: true, envelope };
}

function stripVirtual(t: Task): Task {
  if (!t.virtual) return t;
  const { virtual: _drop, ...rest } = t;
  return rest as Task;
}

function fail(error: string): ValidationResult {
  return { ok: false, error };
}

/** Like {@link checkArray} but an absent (undefined/null) value is OK → []. */
function checkOptionalArray(
  v: unknown,
  field: string,
  guard: (r: Obj) => boolean,
): { rows: Obj[]; error?: string } {
  if (v === undefined || v === null) return { rows: [] };
  return checkArray(v, field, guard);
}

function checkArray(
  v: unknown,
  field: string,
  guard: (r: Obj) => boolean,
): { rows: Obj[]; error?: string } {
  if (!Array.isArray(v)) return { rows: [], error: `"${field}" is missing or not an array` };
  for (let i = 0; i < v.length; i++) {
    const row = v[i];
    if (!isObj(row) || !hasRecordShape(row) || !guard(row)) {
      return { rows: [], error: `"${field}"[${i}] is malformed` };
    }
  }
  return { rows: v as Obj[] };
}

function isTask(r: Obj): boolean {
  return isStr(r.title) && isStrOrNull(r.due_at) && isStr(r.status) && Array.isArray(r.label_ids);
}

function isRule(r: Obj): boolean {
  return isStr(r.rrule) && isStr(r.dtstart) && isStrOrNull(r.until);
}

function isOverride(r: Obj): boolean {
  return isStr(r.recurrence_id) && isStr(r.recurrence_id_ref) && isStr(r.range);
}

function isLabel(r: Obj): boolean {
  return isStr(r.name) && isStrOrNull(r.color);
}

function isTopic(r: Obj): boolean {
  return isStr(r.name) && isStrOrNull(r.parent_id) && isStrOrNull(r.color);
}

function isCalendar(r: Obj): boolean {
  return isStr(r.name) && isStrOrNull(r.color) && typeof r.hidden === "boolean";
}

function isTheme(r: Obj): boolean {
  return isStr(r.preset) && isStr(r.variant) && isStr(r.source);
}
